/**
 * <size-chart-viewer>
 *
 * Renders a PDF onto stacked canvases with pdf.js, so the size chart sits on a themeable background
 * instead of inside the browser's own PDF viewer (whose chrome and background cannot be styled).
 *
 * Attributes:
 *   data-src     the PDF to render
 *   data-lib     asset_url of pdf.min.js
 *   data-worker  asset_url of pdf.worker.min.js
 *
 * The element's initial light DOM is a plain link to the PDF. It stays in place until the render
 * succeeds, so browsers without JS -- or a failed render -- always leave a way to open the chart.
 */
(() => {
  if (customElements.get('size-chart-viewer')) {
    return;
  }

  const MAX_PIXEL_RATIO = 2;
  let libraryPromise = null;

  /**
   * pdf.js spawns its worker from workerSrc. Shopify serves theme assets from the shop domain itself
   * (/cdn/shop/t/<id>/assets/...), so the worker is same-origin and usable directly -- it only needs the
   * protocol-relative asset_url resolved first, since `new Worker()` is stricter about that than a
   * <script src> is. If the worker cannot start, pdf.js falls back to rendering on the main thread.
   */
  const toWorkerSrc = (workerUrl) => {
    try {
      return new URL(workerUrl, window.location.href).href;
    } catch (error) {
      return workerUrl;
    }
  };

  const loadLibrary = (libUrl, workerUrl) => {
    if (libraryPromise) {
      return libraryPromise;
    }

    libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = libUrl;
      script.addEventListener('load', () => {
        if (!window.pdfjsLib) {
          reject(new Error('pdf.js did not register itself'));
          return;
        }

        window.pdfjsLib.GlobalWorkerOptions.workerSrc = toWorkerSrc(workerUrl);
        resolve(window.pdfjsLib);
      });
      script.addEventListener('error', () => reject(new Error('pdf.js could not be loaded')));

      document.head.appendChild(script);
    });

    return libraryPromise;
  };

  class SizeChartViewer extends HTMLElement {
    connectedCallback() {
      if (this.rendered || this.observer) {
        return;
      }

      // The viewer lives inside a drawer or modal, so it only has a measurable width once opened.
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.disconnectObserver();
          this.render();
        }
      });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.disconnectObserver();
    }

    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    }

    async render() {
      if (this.rendered) {
        return;
      }

      this.rendered = true;
      this.removeAttribute('failed');
      this.setAttribute('loading', '');

      try {
        const library = await loadLibrary(this.dataset.lib, this.dataset.worker);
        const document_ = await library.getDocument({ url: this.dataset.src }).promise;
        const width = this.clientWidth || this.parentElement?.clientWidth || 400;
        const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
        const pages = document.createDocumentFragment();

        for (let pageNumber = 1; pageNumber <= document_.numPages; pageNumber++) {
          const page = await document_.getPage(pageNumber);
          const unscaled = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (width / unscaled.width) * pixelRatio });
          const canvas = document.createElement('canvas');

          canvas.className = 'size-chart__page';
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;

          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          page.cleanup();

          pages.appendChild(canvas);
        }

        this.replaceChildren(pages);
        this.removeAttribute('loading');
      } catch (error) {
        // Leave the light DOM link in place; the CSS reveals it again on [failed]
        this.rendered = false;
        this.removeAttribute('loading');
        this.setAttribute('failed', '');
      }
    }
  }

  customElements.define('size-chart-viewer', SizeChartViewer);
})();
